import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Check, Copy, Download } from 'lucide-react';
import { getApiErrorMessage } from '../services/api';
import { rupees } from './shopApi';

function isPhone(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Pay one exact amount by UPI to the farm's account, then report the UTR.
 * On a phone the QR is the way that always works: UPI apps refuse payment
 * links opened from a website for a UPI ID that isn't a merchant's, but they
 * all scan a QR - from another screen or from the phone's own gallery.
 */
export function UpiQrPay({ link, amount, payee, upiId, reference, notReceived, onPaid }: {
  link: string;
  amount: string | number;
  payee: string | null;
  upiId: string | null;
  reference: string; // printed under the saved QR, e.g. the order number
  notReceived?: boolean;
  onPaid: (utr: string) => Promise<unknown>;
}) {
  const phone = isPhone();
  const [utr, setUtr] = useState('');
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qrBox = useRef<HTMLDivElement>(null);

  // The QR as a picture with the amount and reference on it, for the gallery.
  const saveQr = () => {
    const src = qrBox.current?.querySelector('canvas');
    if (!src) return;
    const pad = 40;
    const out = document.createElement('canvas');
    out.width = src.width + pad * 2;
    out.height = src.height + pad * 2 + 110;
    const g = out.getContext('2d')!;
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, out.width, out.height);
    g.drawImage(src, pad, pad);
    g.fillStyle = '#1b361f';
    g.textAlign = 'center';
    g.font = 'bold 44px sans-serif';
    g.fillText(rupees(amount), out.width / 2, src.height + pad + 60);
    g.font = '26px sans-serif';
    g.fillText(`${payee ?? ''} · ${reference}`, out.width / 2, src.height + pad + 100);
    const a = document.createElement('a');
    a.href = out.toDataURL('image/png');
    a.download = `Pay-${reference.replace(/\W+/g, '-')}.png`;
    a.click();
    setSaved(true);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onPaid(utr);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const qr = (
    <div ref={qrBox} className="rounded-lg border border-ink-300/30 bg-white p-2">
      <QRCodeCanvas value={link} size={512} style={{ width: 200, height: 200 }} />
    </div>
  );

  return (
    <section className="flex flex-col gap-3 rounded-2xl border-2 border-brand-green-600 bg-white p-4">
      <div className="text-center">
        <p className="text-sm text-ink-500">Pay to {payee}</p>
        <p className="text-3xl font-extrabold text-brand-green-800 tabular-nums">{rupees(amount)}</p>
        <p className="text-xs text-ink-400">The QR has the exact amount and the reference — please don't change them.</p>
      </div>
      {notReceived && (
        <p className="rounded-lg bg-critical-50 p-2.5 text-sm text-critical-700">We couldn't find your earlier payment. Please check the UPI reference, or pay again.</p>
      )}
      {phone ? (
        <>
          <div className="flex flex-col items-center gap-2">{qr}</div>
          <button type="button" onClick={saveQr} className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-green-700 py-3.5 text-base font-bold text-white">
            <Download className="h-5 w-5" /> {saved ? 'QR saved — save again' : 'Save QR to gallery'}
          </button>
          <ol className="flex flex-col gap-1 rounded-lg bg-brand-green-50 p-3 text-sm text-brand-green-900">
            <li><b>1.</b> Tap <b>Save QR to gallery</b> (or take a screenshot).</li>
            <li><b>2.</b> Open PhonePe / Google Pay / Paytm → <b>Scan</b>.</li>
            <li><b>3.</b> Tap the <b>gallery / photo</b> icon and pick the QR.</li>
            <li><b>4.</b> Pay {rupees(amount)}, then come back here and type the UPI reference below.</li>
          </ol>
          <a href={link} className="text-center text-xs font-medium text-ink-500 underline">Or try opening the UPI app directly (some apps don't allow this)</a>
        </>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-ink-600">Scan this with your phone's UPI app:</p>
          {qr}
        </div>
      )}
      <button type="button" onClick={() => { navigator.clipboard?.writeText(upiId ?? ''); setCopied(true); }} className="mx-auto inline-flex items-center gap-1.5 text-xs text-ink-500">
        UPI ID: <span className="font-semibold text-ink-700">{upiId}</span> {copied ? <Check className="h-3.5 w-3.5 text-success-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>

      <div className="border-t border-dashed border-ink-300/50 pt-3">
        <p className="text-sm font-semibold">Paid? Tell us the UPI reference</p>
        <p className="text-xs text-ink-500">After paying, your UPI app shows a 12-digit number called <b>UPI Ref No.</b> / <b>UTR</b>. Type it here so we can find your payment fast.</p>
        <div className="mt-2 flex gap-2">
          <input value={utr} onChange={(e) => setUtr(e.target.value)} inputMode="numeric" maxLength={25} placeholder="e.g. 412345678901"
            className="min-w-0 flex-1 rounded-lg border border-ink-300/50 px-3 py-2.5 text-base tracking-wider focus:border-brand-green-600 focus:outline-none" />
          <button type="button" disabled={utr.replace(/\s/g, '').length < 10 || busy} onClick={submit}
            className="rounded-lg bg-brand-gold-500 px-4 text-sm font-bold text-white disabled:bg-ink-300">
            {busy ? '…' : 'I have paid'}
          </button>
        </div>
        {error && <p className="mt-1 text-xs font-medium text-critical-600">{error}</p>}
      </div>
    </section>
  );
}
